import type { EncryptableField, EncryptedField, EncryptedPayload } from '@/infra/crypto/payloads'
import { encryptCommandFields } from '@/infra/crypto/payloads'
import { CommandType } from '@/index'
import type { CommandSubmissionPayload } from './commandClient'

const isAlreadyEncrypted = (
  value: EncryptableField | Record<string, unknown> | null,
): value is EncryptedField => typeof value === 'object' && value !== null && 'ciphertext' in value

export const encryptCommand = async (
  payload: CommandSubmissionPayload,
  activeKeyVersion: number,
  encrypt: (data: string | Record<string, unknown>) => Promise<EncryptedPayload>,
): Promise<CommandSubmissionPayload> => {
  // END_SESSION and ARCHIVE_SESSION payloads carry no sensitive fields; skip encryption entirely.
  if (
    payload.commandType === CommandType.END_SESSION ||
    payload.commandType === CommandType.ARCHIVE_SESSION
  )
    return payload

  if (payload.sessionCommand !== null) {
    return encryptSessionCommand(payload, activeKeyVersion, encrypt)
  }

  if (payload.conversationCommand === null) return payload

  const { userPrompt, userAnswers } = payload.conversationCommand

  if (isAlreadyEncrypted(userPrompt) || isAlreadyEncrypted(userAnswers)) return payload

  const plainPrompt: string | null = typeof userPrompt === 'string' ? userPrompt : null
  const plainAnswers: Record<string, unknown> | null =
    userAnswers !== null && typeof userAnswers === 'object' ? userAnswers : null

  const result = await encryptCommandFields(
    { userPrompt: plainPrompt, userAnswers: plainAnswers },
    encrypt,
  )

  if (result === null) return payload

  return {
    ...payload,
    conversationCommand: {
      ...payload.conversationCommand,
      userPrompt: result.userPrompt,
      userAnswers: result.userAnswers,
      encryption: { keyVersion: activeKeyVersion },
    },
  }
}

const encryptSessionCommand = async (
  payload: CommandSubmissionPayload,
  activeKeyVersion: number,
  encrypt: (data: string | Record<string, unknown>) => Promise<EncryptedPayload>,
): Promise<CommandSubmissionPayload> => {
  if (payload.sessionCommand === null) return payload

  const { workDirectory, initialMessage, sessionName } = payload.sessionCommand

  if (
    isAlreadyEncrypted(workDirectory) ||
    isAlreadyEncrypted(initialMessage) ||
    isAlreadyEncrypted(sessionName)
  )
    return payload

  const plainWorkDirectory: string | null = typeof workDirectory === 'string' ? workDirectory : null
  const plainMessage: string | null = typeof initialMessage === 'string' ? initialMessage : null
  const plainSessionName: string | null = typeof sessionName === 'string' ? sessionName : null

  const toEncryptedField = (ep: EncryptedPayload): EncryptedField => ({
    ciphertext: ep.ciphertext,
    iv: ep.iv,
  })

  const [encryptedWorkDir, encryptedMessage, encryptedSessionName] = await Promise.all([
    plainWorkDirectory === null
      ? Promise.resolve(null)
      : encrypt(plainWorkDirectory).then(toEncryptedField),
    plainMessage === null ? Promise.resolve(null) : encrypt(plainMessage).then(toEncryptedField),
    plainSessionName === null
      ? Promise.resolve(null)
      : encrypt(plainSessionName).then(toEncryptedField),
  ])

  return {
    ...payload,
    sessionCommand: {
      ...payload.sessionCommand,
      workDirectory: encryptedWorkDir ?? payload.sessionCommand.workDirectory,
      sessionName: encryptedSessionName ?? payload.sessionCommand.sessionName,
      initialMessage: encryptedMessage ?? payload.sessionCommand.initialMessage,
      encryption: { keyVersion: activeKeyVersion },
    },
  }
}
